import React, { useState } from 'react';
import { X, Download, FileText, Users, Calendar } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { exportHoursToWindow } from '../../../../utils/hoursExportSimple';
import { toLocalYMD } from '../../../../utils/dateFmt';

const HoursExportModal = ({ isOpen, onClose, crew = [], hoursData = {} }) => {
  const [selectedCrewIds, setSelectedCrewIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [exportFormat, setExportFormat] = useState('pdf');
  const [isExporting, setIsExporting] = useState(false);

  if (!isOpen) return null;

  // Show all non-archived crew (includes both On Duty and Off Duty)
  const activeCrew = crew.filter(c => c.status !== 'archived');

  // Handle select all toggle
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCrewIds([]);
      setSelectAll(false);
    } else {
      setSelectedCrewIds(activeCrew.map(c => c.id));
      setSelectAll(true);
    }
  };

  // Handle individual crew selection
  const handleCrewToggle = (crewId) => {
    if (selectedCrewIds.includes(crewId)) {
      setSelectedCrewIds(selectedCrewIds.filter(id => id !== crewId));
      setSelectAll(false);
    } else {
      const newSelection = [...selectedCrewIds, crewId];
      setSelectedCrewIds(newSelection);
      setSelectAll(newSelection.length === activeCrew.length);
    }
  };

  const handleExport = async () => {
    if (selectedCrewIds.length === 0) {
      toast.error(
        'Please select at least one crew member to export. ' +
        'Use the checkboxes to select crew members, then choose an export format.',
        { duration: 5000 }
      );
      return;
    }

    setIsExporting(true);

    try {
      const selectedCrewMembers = activeCrew.filter(c => selectedCrewIds.includes(c.id));

      if (selectedCrewMembers.length === 0) {
        toast.error(
          'No valid crew members selected. The selected crew may not have hours of rest records in the date range. ' +
          'Please adjust your date range or select different crew members.',
          { duration: 6000 }
        );
        return;
      }

      const [year, month] = selectedMonth.split('-');
      const monthKey = selectedMonth;

      if (exportFormat === 'pdf') {
        // Export each crew member as a separate PDF
        for (const crewMember of selectedCrewMembers) {
          // Prepare data for PDF export
          const selectedDate = new Date(parseInt(year), parseInt(month) - 1, 1);
          // FT-32: daysInMonth must be an ARRAY of Date objects, not a number
          const numDays = new Date(parseInt(year), parseInt(month), 0).getDate();
          const daysInMonth = [];
          for (let i = 1; i <= numDays; i++) {
            daysInMonth.push(new Date(parseInt(year), parseInt(month) - 1, i));
          }

          // FT-32: Create getDayHours function that returns the shape expected by hoursExportSimple.js:
          // { hours: ['W'|'R'|'-'...], totalWork, totalRest, isCompliant }
          const getDayHours = (crewId, date) => {
            const dateStr = toLocalYMD(date);
            const memberData = hoursData[crewId] || {};
            const dayData = memberData[dateStr] || {};

            const workHours = dayData.workHours || 0;
            const restHours = dayData.restHours || (workHours ? 24 - workHours : 24);
            const isCompliant = restHours >= 10;

            // Build 24-hour array from periods if available, otherwise estimate from work hours
            let hours = Array(24).fill('R'); // Default to all rest
            if (dayData.periods && Array.isArray(dayData.periods)) {
              // Parse actual periods data
              dayData.periods.forEach(period => {
                const start = parseInt(period.startHour || period.start || 0);
                const end = parseInt(period.endHour || period.end || 0);
                const type = period.type === 'work' ? 'W' : 'R';
                for (let h = start; h < end && h < 24; h++) {
                  hours[h] = type;
                }
              });
            } else if (workHours > 0) {
              // Estimate work hours (simple assumption: work starts at 8am)
              for (let h = 8; h < 8 + workHours && h < 24; h++) {
                hours[h] = 'W';
              }
            }

            return {
              hours,
              totalWork: workHours,
              totalRest: restHours,
              isCompliant
            };
          };

          // Calculate summary
          const memberData = hoursData[crewMember.id] || {};
          let totalWork = 0;
          let totalRest = 0;
          let violations = 0;

          for (let i = 1; i <= numDays; i++) {
            const date = new Date(parseInt(year), parseInt(month) - 1, i);
            const dayData = getDayHours(crewMember.id, date);
            totalWork += dayData.totalWork;
            totalRest += dayData.totalRest;
            if (!dayData.isCompliant && dayData.totalWork > 0) violations++;
          }
          
          const summary = { totalWork, totalRest, violations };
          
          // Get signatures if available
          const signatureKey = `hor_signatures_${crewMember.id}_${monthKey}`;
          const signatures = JSON.parse(localStorage.getItem(signatureKey) || '{}');
          
          // Export as PDF using existing utility
          // Add a small delay between exports if multiple
          if (selectedCrewMembers.length > 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          exportHoursToWindow(
            crewMember.id,
            crewMember,
            selectedDate,
            daysInMonth,
            getDayHours,
            summary,
            signatures
          );
        }
      } else {
        // Export as JSON - combine all selected crew data
        const exportData = {
          vessel: 'Current Vessel', // You could add vessel name here
          month: selectedMonth,
          exportDate: toLocalYMD(new Date()),
          crewRecords: []
        };

        // Add each crew member's data
        for (const crewMember of selectedCrewMembers) {
          const memberData = hoursData[crewMember.id] || {};
          const crewRecord = {
            crew: {
              id: crewMember.id,
              name: crewMember.full_name || crewMember.name,
              position: crewMember.position
            },
            records: {}
          };

          // Filter data for selected month
          Object.keys(memberData).forEach(date => {
            if (date.startsWith(monthKey)) {
              crewRecord.records[date] = memberData[date];
            }
          });

          exportData.crewRecords.push(crewRecord);
        }

        const dataStr = JSON.stringify(exportData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        const filename = selectedCrewMembers.length > 1 
          ? `hours-of-rest-multiple-${monthKey}.json`
          : `hours-of-rest-${selectedCrewMembers[0].name.replace(/\s+/g, '-')}-${monthKey}.json`;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      onClose();
    } catch (error) {

      toast.error(
        `Export failed: ${error.message}. ` +
        `This may be due to browser permissions or file system issues. ` +
        `Try exporting to a different format (PDF or CSV) or check your browser's download settings.`,
        { duration: 8000 }
      );
    } finally {
      setIsExporting(false);
    }
  };

  // Generate list of available months (last 12 months)
  const getAvailableMonths = () => {
    const months = [];
    const now = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      months.push({ value, label });
    }
    
    return months;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <Download className="h-6 w-6 text-cyan-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Export Hours of Rest</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Crew Member Selection */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-900 mb-3">
                <Users className="h-4 w-4 mr-2 text-gray-400" />
                Select Crew Members
                <span className="ml-2 text-xs text-gray-500">
                  ({selectedCrewIds.length} selected)
                </span>
              </label>
              
              {/* Select All Checkbox */}
              <div className="mb-2 pb-2 border-b border-gray-200">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-900">Select All</span>
                </label>
              </div>
              
              {/* Individual Crew Checkboxes */}
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {activeCrew.length === 0 ? (
                  <p className="text-sm text-gray-500">No active crew members available</p>
                ) : (
                  activeCrew.map(member => (
                    <label key={member.id} className="flex items-center hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={selectedCrewIds.includes(member.id)}
                        onChange={() => handleCrewToggle(member.id)}
                        className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-900">
                        {member.full_name || member.name}
                        <span className="text-gray-500 ml-1">- {member.position}</span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Month Selection */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-900 mb-2">
                <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                Select Month
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="block w-full rounded-md px-3 py-2 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600"
              >
                {getAvailableMonths().map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Export Format */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-900 mb-2">
                <FileText className="h-4 w-4 mr-2 text-gray-400" />
                Export Format
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="pdf"
                    checked={exportFormat === 'pdf'}
                    onChange={(e) => setExportFormat(e.target.value)}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-900">PDF Document (for printing/signing)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="json"
                    checked={exportFormat === 'json'}
                    onChange={(e) => setExportFormat(e.target.value)}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-900">JSON Data (for backup/import)</span>
                </label>
              </div>
            </div>

            {/* Info Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                {exportFormat === 'pdf' 
                  ? selectedCrewIds.length > 1 
                    ? `Will generate ${selectedCrewIds.length} separate PDF files, one for each crew member selected.`
                    : 'PDF export will include the full monthly record with signature fields for crew and authorized person.'
                  : selectedCrewIds.length > 1
                    ? 'JSON export will combine all selected crew members\' data in a single file.'
                    : 'JSON export contains raw data that can be imported back into the system or used for analysis.'
                }
              </p>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={selectedCrewIds.length === 0 || isExporting}
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export {selectedCrewIds.length > 0 && `(${selectedCrewIds.length})`}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HoursExportModal;